import { css } from '@emotion/react';
import { BoundType, Cart } from '@flysas-tech/book-value-stream-models';
import { ActionChip, Dialog, Divider, semanticTokens, Tag, TagProps, tokens } from '@flysas-tech/commercial-ui';
import {
  BaggageShortRuleList,
  DisplayId,
  FareInformation,
  getModalTitleForFareInformation,
  OrdinaryShortRules,
  ProductCardHeader,
  RebookConditionShortRuleList,
  TicketRules,
  cleanTestId,
  useTicketRules,
  ProductCard,
  RuleCategory,
  getShortRulesByCategory,
} from 'common';
import { t } from 'i18n';
import { Fragment, ReactNode, useState } from 'react';
import { getCurrencyByPos, hasSelectedBounds, isDefined } from 'shared';

import { Fetch } from '../../../hooks/useTicketRules';

const styles = {
  container: css`
    position: relative;
  `,
  wrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  `,
  product: (isSelected: boolean) => css`
    display: grid;
    height: 100%;
    width: 100%;
    grid-template-rows: auto 1fr;
    border-radius: ${tokens.dimension['space-5']};
    box-shadow: ${tokens.shadow['small-strong']};
    outline: 2px solid ${isSelected ? semanticTokens.color.border.active : 'none'};
    &:hover {
      box-shadow: ${tokens.shadow['medium-strong']};
      outline: 1px solid ${isSelected ? 'none' : semanticTokens.color.border.neutral};
    }
  `,

  productFocus: css`
    height: 100%;
    cursor: pointer;
    width: 100%;
    position: relative;
    border-radius: ${tokens.dimension['space-5']};
    &:has(input:focus-visible) {
      outline: 2px solid ${semanticTokens.color.border.focus};
      outline-offset: 3px;
    }
  `,

  icon: css`
    display: flex;
    justify-content: center;
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    text-decoration: underline;
  `,
  ticketInfoContainer: css`
    margin-bottom: ${tokens.dimension['space-3']};
    padding-left: ${tokens.dimension['space-5']};
    padding-right: ${tokens.dimension['space-5']};
    display: flex;
    justify-content: center;
    flex-direction: column;
  `,
  shortRulesContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-5']};
    padding: ${tokens.dimension['space-4']} ${tokens.dimension['space-6']};
  `,
  pillContainer: css`
    display: flex;
    position: absolute;
    gap: ${tokens.dimension['space-3']};
    top: -15px;
    left: 20px;
  `,
};

type Props = {
  card: ProductCard;
  boundType: BoundType;
  isSelected: boolean;
  handleProductClick: (selectedId: string) => void;
  children?: ReactNode;
  showBaggageRules?: boolean;
  showOrdinaryRules?: boolean;
  localCart: Cart;
  fetchTicketRules: Fetch;
  accrual?: {
    data: {
      bonusPoints: number;
      levelPoints: number;
    };
    isLoading: boolean;
  };
};

export const ProductCardComponent = ({
  card,
  isSelected,
  boundType,
  handleProductClick,
  children,
  localCart,
  accrual,
  showBaggageRules = true,
  showOrdinaryRules = true,
  fetchTicketRules,
}: Props) => {
  const { id, isPopularChoice, shortRules, longRules, productName, cabinPrefix, cabinName, price, availableSeats } =
    card;
  const [showModal, setShowModal] = useState(false);

  const { pointOfSale, travelers, journeys, officeId, cepData } = localCart;
  const isBoundsSelected = hasSelectedBounds(localCart);

  const currency = getCurrencyByPos(pointOfSale);
  const shouldDisplayAvailableSeats = availableSeats < 9;

  const [currentDisplayId, setCurrentDisplayId] = useState<DisplayId>(() => DisplayId.FareInformation);

  const { data: ticketRules } = useTicketRules({
    enabled: currentDisplayId === DisplayId.TicketRules,
    journeys,
    travelers,
    officeId,
    cepId: cepData?.cepId,
    fetcher: fetchTicketRules,
  });

  const onClickHandler = (displayId: DisplayId) => () => {
    setCurrentDisplayId(displayId);
  };

  const onDialogClose = () => {
    setCurrentDisplayId(DisplayId.FareInformation);
    setShowModal(false);
  };

  const onDialogBack = () => {
    currentDisplayId === DisplayId.TicketRules ? setCurrentDisplayId(DisplayId.FareInformation) : undefined;
  };

  const testId = `upsell-product-card-${cleanTestId(`${cabinName} ${productName}`)}`;

  const popularLabel = {
    color: 'critical',
    label: t('popular', { defaultValue: 'Popular' }),
  } as const;
  const seatsLeftLabel = {
    color: 'warning',
    label: t('seats.left', { seats: availableSeats }),
  } as const;

  const labels: { color: TagProps['color']; label: string }[] = [
    isPopularChoice ? popularLabel : undefined,
    shouldDisplayAvailableSeats ? seatsLeftLabel : undefined,
  ].filter(isDefined);

  return (
    <Fragment>
      <div css={styles.productFocus}>
        <div
          css={styles.product(isSelected)}
          data-testid={testId}
          onClick={e => {
            e.stopPropagation();
            handleProductClick(id);
          }}
        >
          <div css={styles.pillContainer}>
            {labels.map(({ color, label }, index) => (
              <Tag key={`${label}-${index}`} color={color} label={label} size="xs"></Tag>
            ))}
          </div>

          <ProductCardHeader
            card={card}
            price={price}
            currency={currency.CURRENCY_CODE}
            isSelected={isSelected}
            handleProductClick={handleProductClick}
            productId={id}
            boundType={boundType}
            labels={labels.map(({ label }) => label)}
            {...(accrual ? { accrual } : {})}
            pointOfSale={pointOfSale}
          />

          <div css={styles.wrapper}>
            <div css={styles.shortRulesContainer}>
              <RebookConditionShortRuleList
                shortRules={getShortRulesByCategory(shortRules, RuleCategory.RefundAndRebook)}
              />
              <Divider variant="subtle" direction="horizontal" />

              {showBaggageRules && (
                <>
                  <BaggageShortRuleList shortRules={getShortRulesByCategory(shortRules, RuleCategory.Baggage)} />
                  <Divider variant="subtle" direction="horizontal" />
                </>
              )}

              {showOrdinaryRules && (
                <>
                  <OrdinaryShortRules shortRules={getShortRulesByCategory(shortRules, RuleCategory.Ordinary)} />
                  {children && <Divider variant="subtle" direction="horizontal" />}
                </>
              )}

              {children}
            </div>

            <div css={styles.ticketInfoContainer}>
              <Divider variant="subtle" direction="horizontal" />
              <ActionChip
                tabIndex={isSelected ? 0 : -1}
                icon="InfoCircle"
                label={t('common:showMoreInformation', { defaultValue: 'Show more information' })}
                size="large"
                border={false}
                css={styles.icon}
                onClick={() => setShowModal(true)}
              />
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <Dialog.Root>
          <Dialog.Window
            onCloseLabel="Close"
            onBackLabel="Back"
            onClose={() => onDialogClose()}
            title={getModalTitleForFareInformation({
              id: currentDisplayId,
              cabinName: cabinPrefix ? `${cabinPrefix} ${cabinName} ${productName}` : productName.toUpperCase(),
            })}
            onBack={() => onDialogBack()}
          >
            <Dialog.Container>
              {currentDisplayId === DisplayId.FareInformation && (
                <FareInformation
                  shortRules={shortRules}
                  longRules={longRules}
                  onClickHandler={onClickHandler}
                  ticketRulesDisplayId={DisplayId.TicketRules}
                  isBoundsSelected={isBoundsSelected}
                />
              )}

              {currentDisplayId === DisplayId.TicketRules && <TicketRules rules={ticketRules?.rules} />}
            </Dialog.Container>
          </Dialog.Window>
        </Dialog.Root>
      )}
    </Fragment>
  );
};
